/**
 * @file app-insights.service.ts
 */
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { UserService } from '../user/user.service';
import { MemberService } from '../member/member.service';
import { TokenService } from 'src/app/core/services/token/token.service';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

@Injectable({
  providedIn: 'root',
})
export class AppInsightsService {
  private appInsights!: ApplicationInsights;

  constructor(
    private readonly userService: UserService,
    private readonly memberService: MemberService,
    private readonly tokenService: TokenService
  ) {
    // Initialize Application Insights
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.instrumentationKey, // Replace with your Azure Application Insights Instrumentation Key
        enableAutoRouteTracking: true, // This will automatically track page views (you can disable this if needed)
        enableAjaxErrorStatusText: true,
        maxBatchInterval: 10000, // Adjust this to control the batch interval
        disableFetchTracking: false, // Enable fetch tracking (optional)
      },
    });

    // Initialize Application Insights
    this.appInsights.loadAppInsights();

    this.appInsights.config.isBrowserLinkTrackingEnabled = true;
    this.appInsights.config.autoTrackPageVisitTime = true;
    this.appInsights.context.location.ip = '';
  }

  setUsernameForAppInsights(username: string) {
    this.appInsights.context.user.id = username;
  }

  setClientIdForAppInsights(clientId: string) {
    this.appInsights.context.user.accountId = clientId;
  }

  getDate(): string {
    var dateToday = new Date();
    return `${dateToday.getFullYear()}/${
      dateToday.getMonth() + 1
    }/${dateToday.getDate()}`;
  }

  getName(): string {
    return `${
      this.userService.member?.firstName
        ? this.userService.member?.firstName
        : this.memberService.user?.firstName
        ? this.memberService.user?.firstName
        : 'unknown'
    } ${
      this.userService.member?.lastName
        ? this.userService.member?.lastName
        : this.memberService.user?.lastName
        ? this.memberService.user?.lastName
        : 'unknown'
    }`;
  }

  trackPageView(name: string, additionalProperties?: any, uri?: string): void {
    // option to call manually
    const userDetails = {
      clientID: this.appInsights.context.user.accountId,
      userID: this.appInsights.context.user.id,
      CardID:
        this.userService.eligibilityVerificationResponse?.cardId || 'unknown',
      ClientName: this.userService.getClientName()
        ? this.userService.getClientName()
        : 'unknown',
      ProviderType: this.tokenService.getProviderType()
        ? this.tokenService.getProviderType()
        : 'unknown',
      isMemberMigrated: this.tokenService.getIsMigratedMember(),
      Date: this.getDate(),
      Email: this.memberService?.user?.email,
      IsExistingUser: !this.memberService?.user?.isProfileVerified,
      Name: this.getName(),
    };
    const properties = { ...userDetails, ...additionalProperties };
    this.appInsights.trackPageView({ name, uri, properties });
  }

  trackPageViewWithoutClientId(
    name: string,
    properties: { [key: string]: any } = {
      Platform: 'web',
      Date: this.getDate(),
    },
    uri?: string
  ): void {
    // option to call manually
    this.appInsights.trackPageView({ name, uri, properties });
  }

  trackPageViewWithEmailProperties(
    name: string,
    email: string,
    isError: boolean,
    errorMsg?: string,
    properties: { [key: string]: any } = isError
      ? { Email: email, Platform: 'web', Date: this.getDate(), Error: errorMsg }
      : { Email: email, Platform: 'web', Date: this.getDate() },
    uri?: string
  ): void {
    // option to call manually
    this.appInsights.trackPageView({ name, uri, properties });
  }

  trackEvent(
    name: string,
    properties: { [key: string]: any } = {
      clientID: this.appInsights.context.user.accountId,
      userID: this.appInsights.context.user.id,
      CardID:
        this.userService.eligibilityVerificationResponse?.cardId || 'unknown',
      ClientName: this.userService.getClientName()
        ? this.userService.getClientName()
        : 'unknown',
      ProviderType: this.tokenService.getProviderType()
        ? this.tokenService.getProviderType()
        : 'unknown',
      isMemberMigrated: this.tokenService.getIsMigratedMember(),
      Date: this.getDate(),
      Email: this.memberService.user.email,
      IsExistingUser: !this.memberService.user.isProfileVerified,
      Name: this.getName(),
    }
  ): void {
    this.appInsights.trackEvent({ name }, properties);
  }

  trackException(exception: Error, severityLevel?: number): void {
    this.appInsights.trackException({ exception, severityLevel });
  }

  // Log error
  logError(error: any) {
    this.appInsights.trackException({ exception: error });
  }
}
